import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Card, CardBody, CardHeader, Row, Col, Spinner, Alert, Container, Badge } from 'react-bootstrap';
import QRCode from 'react-qr-code';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import PageTitle from '../components/PageTitle';
import Footer from '../components/Footer';
import { resolveConferenceTypeDisplay, toTransparentBadgeStyle } from '../constants/conferenceTypes';

const PublicQRCodePage = () => {
  const { maskedCode } = useParams();
  const [session, setSession] = useState(null);
  const [conference, setConference] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [qrUrl, setQrUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [copiedZoom, setCopiedZoom] = useState(false);
  const [theme, setTheme] = useState(() => {
    if (typeof window === 'undefined') return 'light';
    const bodyAttr = document.body.getAttribute('data-bs-theme');
    if (bodyAttr === 'dark' || bodyAttr === 'light') return bodyAttr;
    const stored = localStorage.getItem('theme');
    if (stored === 'dark' || stored === 'light') return stored;
    return 'light';
  });

  // Keep Bootstrap theme attribute and localStorage in sync with local theme state
  useEffect(() => {
    if (typeof window !== 'undefined') {
      document.body.setAttribute('data-bs-theme', theme === 'dark' ? 'dark' : 'light');
      localStorage.setItem('theme', theme === 'dark' ? 'dark' : 'light');
    }
  }, [theme]);

  useEffect(() => {
    if (maskedCode) {
      fetchSessionData();
    }
  }, [maskedCode]);

  useEffect(() => {
    if (session && maskedCode) {
      const baseUrl = window.location.origin;
      const attendanceUrl = `${baseUrl}/attendance/${maskedCode}`;
      setQrUrl(attendanceUrl);
    }
  }, [session, maskedCode]);

  const fetchSessionData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await axios.get(`${API_BASE_URL}/api/conference-sessions/masked-attendance/${maskedCode}/public`);
      
      if (response.data.session) {
        setSession(response.data.session);
        setConference(response.data.conference);
      } else {
        setError('Session not found or not available for public viewing.');
      }
    } catch (error) {
      console.error('Error fetching session data:', error);
      const backendMessage = error.response?.data?.error || error.response?.data?.message;

      if (backendMessage && backendMessage.toLowerCase().includes('already passed')) {
        // Only show the "elapsed" message when the backend explicitly reports that the session is over
        setError('This session has elapsed or is no longer available for viewing.');
      } else {
        // Otherwise show a more generic / backend-specific error
        setError(backendMessage || 'Failed to load session information. Please check the QR code or try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    
    try {
      const [hours, minutes] = timeString.split(':');
      const hour = parseInt(hours);
      const minute = parseInt(minutes);
      
      const period = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      const displayMinute = minute.toString().padStart(2, '0');
      
      return `${displayHour}:${displayMinute} ${period}`;
    } catch (error) {
      console.error('Error formatting time:', error);
      return timeString;
    }
  };

  const renderConferenceType = (type) => {
    const typeOptions = Array.isArray(conference?.creator?.conferenceTypePreferences)
      ? conference.creator.conferenceTypePreferences
      : undefined;
    const resolved = resolveConferenceTypeDisplay(type, typeOptions);
    return {
      label: resolved.label || (type ? type : 'Conference'),
      name: resolved.name || (type ? type : 'Conference'),
      color: resolved.color
    };
  };

  const zoomLink = conference?.zoomLink || session?.zoomLink;
  const conferenceTitle = conference?.title || session?.title || 'Conference Session';
  const conferenceTypeInfo = renderConferenceType(conference?.type || session?.type);
  const conferenceStartTime = conference?.startTime || session?.startTime;
  const conferenceEndTime = conference?.endTime || session?.endTime;

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(qrUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = qrUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleEmailShare = () => {
    const baseUrl = window.location.origin;
    const qrPageUrl = maskedCode ? `${baseUrl}/qr/${maskedCode}` : '';
    const subject = `Conference Attendance QR Code - ${conference?.title}`;
    const body = `Hello,

Please use the following QR code or URL to record your attendance for the conference session:

Conference: ${conference?.title}
Date: ${session?.date ? new Date(session.date + 'T00:00:00').toLocaleDateString('en-US', { 
  weekday: 'long', 
  year: 'numeric', 
  month: 'long', 
  day: 'numeric' 
}) : ''}
${conference?.startTime && conference?.endTime ? `Time: ${formatTime(conference.startTime)} - ${formatTime(conference.endTime)}` : ''}
${session?.location ? `Location: ${session.location}` : ''}
Attendance Code: ${session?.attendanceCode || ''}

Attendance Page URL: ${qrUrl}
${qrPageUrl ? `QR Code Display Page: ${qrPageUrl}` : ''}

You can either:
1. Scan the QR code with your phone camera
2. Click the attendance URL above to access the attendance page
3. Click the QR code display page to view the QR code and conference details
4. Share these URLs with others who need to record attendance

Best regards`;

    const mailtoLink = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailtoLink, '_blank');
  };

  const handleCopyZoomLink = async () => {
    if (!zoomLink) return;
    try {
      await navigator.clipboard.writeText(zoomLink);
      setCopiedZoom(true);
      setTimeout(() => setCopiedZoom(false), 2000);
    } catch (err) {
      console.error('Failed to copy Zoom link:', err);
      const textArea = document.createElement('textarea');
      textArea.value = zoomLink;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedZoom(true);
      setTimeout(() => setCopiedZoom(false), 2000);
    }
  };

  if (loading) {
    return (
        <> 
      <div  className=" min-vh-100 d-flex align-items-center justify-content-center"
      >
        <div className="text-center">
          <Spinner animation="border" variant="primary" size="lg" />
          <p className="text-muted mt-3">Loading conference information...</p>
        </div>
      </div>
      </>
    );
  }

  if (error || !session) {
    return (
        <>
          
      <div  className=" min-vh-100 d-flex align-items-center justify-content-center">
        <div style={{ maxWidth: '600px', width: '100%' }}>
          <Card className="border-0 shadow-sm p-4">
          <Alert variant="danger" className="border-0 mb-0">
            <span className="fw-semibold">Session Not Available</span>
            <p className="mb-0">{error + (error ? '. ' : '') + 'This session has elapsed or is no longer available for viewing.'}</p>
          </Alert>

          </Card>          
          <div className="text-end mt-3">
          <Button variant="light-lite" size="sm" onClick={() => window.location.href = "/"}>
             Return to Home<i className="bi bi-arrow-right ms-2"></i>
          </Button>
        </div>
        </div>
      </div>
      </>
    );
  }

  return (
    <>
      <Container>
        <Row className="justify-content-center mb-5">
          <Col lg={6} md={6} className=" py-3 border-bottom" style={{ maxWidth: '960px', width: '100%' }}>
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <img
                  src={theme === 'light' ? "/images/asset5.svg" : "/images/asset4.svg"}
                  alt="brand logo"
                  style={{ height: "16px", borderRight: "2px solid var(--gray-400)", paddingRight: "10px"}}
                />
                <div className="ms-2 fw-semibold text-truncate">Conference Attendance</div>
              </div>            
            <div>
                
                <Button
                  variant="light-lite"
                  size="sm"
                  className="me-2"
                  onClick={() => {
                    const newTheme = theme === 'light' ? 'dark' : 'light';
                    setTheme(newTheme);
                  }}
                  title="Toggle light/dark mode"
                >
                  <i className={theme === 'light' ? "bi bi-moon-stars-fill" : "bi bi-brightness-high-fill"}></i>
                </Button>
                <Button variant="light-lite" size="sm" onClick={() => {
                  window.location.href = "/";
                }}>
                  <span className="d-none d-md-inline me-2">Home</span>
                  <i className="bi bi-arrow-right"></i>
                </Button>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
      <Container fluid className="d-flex align-items-center justify-content-center" style={{Height: 'calc(100vh - 72px)'}}>
        <PageTitle title="Conference Attendance" description="Scan this QR code to record your attendance" />
       
        <Row className="w-100 justify-content-center">
          <Col lg={6} md={6} className="text-center" style={{ maxWidth: '960px', width: '100%' }}>
        
          
          {/* QR Code and URL in Full Screen */}
          <Row className="mb-5">
            <Col md={6} className="mb-4 mb-md-0">
            <Card className="border-0 shadow-sm h-100">
              <CardHeader className="border-0 px-4 py-3 pt-4 d-flex align-items-center justify-content-start gap-3">
                <span className=" bg-primary rounded-3 p-2" style={{width:'40px', height:'40px'}}><i className="bi bi-qr-code"></i></span>
                <h6 className="fw-bold mb-0">QR Code</h6>
              </CardHeader>
              <CardBody className="px-4 pb-5 pt-2 d-flex flex-column align-items-center justify-content-center" >
                <QRCode className=" rounded-4 p-4 mb-3" style={{ border:'3px solid var(--gray-200)', background: '#fff' }}
                  value={qrUrl}
                  level="H"
                  includeMargin={true}
                />
                <Button variant="outline-primary" size="sm" className="mb-2" onClick={() => window.open(qrUrl, '_blank')}>
                  <i className="bi bi-clipboard me-2"></i>
                  Open URL
                </Button>                
                <p className="text-muted small mb-0">Point your camera at the QR code to check in</p>
              </CardBody>
            </Card>
            </Col>
           
          
            <Col md={6} className="d-flex flex-column align-items-center">
              <Card className="border-0 shadow-sm w-100 h-100 mb-3">
                <CardHeader className="border-0 px-4 py-3 pt-4 d-flex align-items-center justify-content-start gap-3">
                  <span className=" bg-secondary rounded-3 p-2" style={{width:'40px', height:'40px'}}><i className="bi bi-link"></i></span>
                  <h6 className="fw-bold mb-0">QR Code URL</h6>
                </CardHeader>
                <CardBody className="px-4 py-0" >
                  <div className="d-flex align-items-center bg-primary border p-2 justify-content-between rounded mb-2 p-2 w-100">
                  <code className="d-block align-items-center text-truncate bg-primary">
                    <mark className="bg-primary p-0">{qrUrl}</mark>
                  
                  </code>
                  <Button
                    variant="light"
                    size="sm"
                    onClick={handleCopyUrl}
                    className="py-1 px-2"
                    title="Copy URL"
                  >
                    {copied ? (
                      <i className="bi bi-check text-success"></i>
                    ) : (
                      <i className="bi bi-clipboard"></i>
                    )}
                  </Button>
                  </div>
              
               
                <Button 
                  variant="outline-primary" 
                  size="sm" 
                  onClick={handleEmailShare}
                  className="w-100 mb-2"
                >
                  <i className="bi bi-envelope me-2"></i>
                  Share via Email
                </Button>
                 <p className="text-muted small mb-4">
                  Share this URL directly or scan the QR code
                </p>
                </CardBody>
                </Card>

                <Card className="border-0 shadow-sm w-100 h-100">
                  <CardHeader className="border-0 px-4 py-3 pt-4 d-flex align-items-center justify-content-start gap-3">
                    <span className=" bg-success rounded-3 p-2" style={{width:'40px', height:'40px'}}><i className="bi bi-code"></i></span>
                    <h6 className="fw-bold mb-0">Attendance Code</h6>
                  </CardHeader>
                  <CardBody className="px-4 py-0" >
                    <div className="d-flex align-items-center  flex-column justify-content-center position-relative rounded-3 mb-2 w-100"
                    style={{
                      background:
                        "linear-gradient(45deg, var(--blue) 0%, var(--green) 70%, var(--cyan) 100%)",
                    }}>
                    <code className="d-flex align-items-center h1 fw-bold text-white py-2 m-0" style={{ backgroundColor: 'transparent' }}>
                    {session.attendanceCode}
                    </code>
                    <div className="w-100 px-0 rounded-bottom-2 absolute bottom-0 left-0 right-0 d-flex  align-items-center justify-content-center" 
                              style={{ background: "linear-gradient(45deg, transparent 0%, var(--card-bg) 100%)",
                                  height: "10px",
                              }}></div>
                    </div>
                    <p className="text-muted small mb-3">
                      Enter this code to check in
                    </p>
                  </CardBody>
                </Card>
            </Col>
          </Row>

          {/* Conference Details in Full Screen */}
          <div className="mb-4">
            <Card className="border-0 rounded-3 p-2 shadow-sm">
              <CardHeader className="border-0 text-start bg-transparent">
                <div className="d-flex justify-content-between align-items-center pt-2">
                  <h6 className="mb-0 fw-semibold">Conference Details</h6>
                  <span
                    className="badge rounded-pill px-3 py-2"
                    style={toTransparentBadgeStyle(conferenceTypeInfo.color)}
                    title={conferenceTypeInfo.name}
                  >
                    {conferenceTypeInfo.label}
                  </span>
                </div>
              </CardHeader>
              <CardBody className="px-3 pt-0">
                <Row className="g-4 align-items-stretch">
                  <Col lg={ zoomLink ? 8 : 12}>
                  <div className="mb-3 text-start">
                      <h4 className="fw-bold mb-1">{conferenceTitle}</h4>
                    </div>
                    <Row className="g-3">
                    {session.speaker && (
                        <Col md={7}>
                          <div className="text-start d-flex align-items-center px-3 gap-2">
                            <span className="d-inline-flex align-items-center justify-content-center rounded-3 bg-primary bg-opacity-10" style={{ width: 40, height: 40 }}>
                              <i className="bi bi-person-fill text-primary"></i>
                            </span>
                            <div className="d-flex flex-column align-items-start justify-content-start">
                            <div className="text-muted text-uppercase small fw-semibold">Speaker</div>
                            <div className="fw-medium">
                              {session.speaker}
                              {session.speakerTitle && ` (${session.speakerTitle})`}
                            </div>
                            </div>
                          </div>
                        </Col>
                      )}
                      {session.location && (
                        <Col md={5}>
                          <div className="text-start d-flex align-items-center px-3 gap-2">
                            <span className="d-inline-flex align-items-center justify-content-center rounded-3 bg-danger bg-opacity-10" style={{ width: 40, height: 40 }}>
                              <i className="bi bi-geo-alt text-danger"></i>
                            </span>
                            <div className="d-flex flex-column align-items-start justify-content-start">
                            <div className="text-muted text-uppercase small fw-semibold">Location</div>
                            <div className="fw-medium">{session.location}</div>
                            </div>
                          </div>
                        </Col>
                      )}
                      
                      <Col md={7}>
                        <div className="d-flex align-items-start gap-2 p-3 rounded-3 bg-light">
                          <div className="d-inline-flex align-items-center justify-content-center rounded-3 bg-cyan bg-opacity-10" style={{ width: 40, height: 40 }}>
                            <i className="bi bi-calendar-event text-cyan"></i>
                          </div>
                          <div className="text-start">
                            <div className="text-muted text-uppercase small fw-semibold">Date</div>
                            <div className="fw-semibold">
                              {new Date(session.date + 'T00:00:00').toLocaleDateString('en-US', { 
                                weekday: 'long', 
                                year: 'numeric', 
                                month: 'long', 
                                day: 'numeric' 
                              })}
                            </div>
                          </div>
                        </div>
                      </Col>
                      <Col md={5}>
                        <div className="d-flex align-items-start gap-3 p-3 rounded-3 bg-light">
                          <div className="d-inline-flex align-items-center justify-content-center rounded-3 bg-info bg-opacity-10" style={{ width: 40, height: 40 }}>
                            <i className="bi bi-clock text-info"></i>
                          </div>
                          <div className="text-start">
                            <div className="text-muted text-uppercase small fw-semibold">Time</div>
                            <div className="fw-semibold">
                              {conferenceStartTime && conferenceEndTime
                                ? `${formatTime(conferenceStartTime)} - ${formatTime(conferenceEndTime)}`
                                : 'Time TBD'}
                            </div>
                          </div>
                        </div>
                      </Col>

                    </Row>
                  </Col>

                  {zoomLink && (
                    <Col lg={ zoomLink ? 4 : 0}>
                      <div
                        className="h-100 d-flex flex-column justify-content-between rounded-3 p-4"
                        style={{
                          background: 'linear-gradient(135deg, var(--blue-light) 0%, var(--blue-lighter) 100%)',
                          border: '1px solid rgba(59,130,246,0.15)'
                        }}
                      >
                        <div className="text-start mb-3">
                          <div className="text-muted text-uppercase small fw-semibold">Virtual Meeting</div>
                          <h6 className="fw-semibold mb-0">Zoom Conference</h6>
                        </div>
                        <div className="d-grid gap-2">
                          <Button
                            variant="primary"
                            size="sm"
                            href={zoomLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="fw-semibold py-2"
                          >
                            <i className="bi bi-camera-video me-2"></i>
                            Join Zoom Meeting
                          </Button>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={handleCopyZoomLink}
                            className="fw-semibold py-2"
                          >
                            <i className={`bi ${copiedZoom ? 'bi-check-lg text-success' : 'bi-link-45deg' } me-2`}></i>
                            {copiedZoom ? 'Copied Zoom Link' : 'Copy Zoom Link'}
                          </Button>
                        </div>
                      </div>
                    </Col>
                  )}
                </Row>
                {session.description && (
                  <Row className="mt-3">
                    <Col>
                      <div className="mb-0 text-start">
                        <label className="form-label text-muted small fw-semibold mb-1">Description</label>
                        <p className="mb-0 fw-medium">{session.description}</p>
                      </div>
                    </Col>
                  </Row>
                )}
                
                <Row className="mt-4">
                  <Col>
                    <div
                      className="p-4 rounded-3 text-start"
                      style={{
                        background: 'rgba(59,130,246,0.06)',
                        border: '1px solid rgba(59,130,246,0.15)'
                      }}
                    >
                      <div className="d-flex align-items-start gap-3">
                        <div className="d-inline-flex align-items-center justify-content-center rounded-3 bg-primary bg-opacity-10" style={{ width: 40, height: 40 }}>
                          <i className="bi bi-info-circle text-primary"></i>
                        </div>
                        <div>
                          <h6 className="fw-semibold mb-2">How to Check In</h6>
                          <ul className="mb-0 small text-muted">
                            <li>Scan the QR code with your device camera</li>
                            <li>Or visit the attendance URL and enter the code</li>
                            <li>Check-in opens 15 minutes before the conference starts</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>

              </CardBody>
            </Card>
          </div>
        
        </Col>
        </Row>
      </Container>
      <Footer />
      
    </>
  );
};

export default PublicQRCodePage;

